import type { HTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";

import { cn } from "@/lib/utils";

/**
 * Sticky-note card for canvas/workshop surfaces (SWOT walls, impact/effort
 * grids). Tinted like real sticky paper with a soft lifted shadow; `tone`
 * carries meaning (board color or impact-category legend), so pick it from the
 * domain, not for decoration. Purely presentational — drag behavior, dialogs,
 * and menus belong to the caller (spread handlers via the div props).
 */
export interface StickyNoteProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  description?: string | null;
  /** Small attribution line at the bottom (e.g. the author's name). */
  footer?: ReactNode;
  /** Chips/icons row rendered between title and description. */
  adornment?: ReactNode;
  tone?: "yellow" | "green" | "blue" | "orange" | "red" | "gray";
  /** Highlight ring for chosen/selected notes. */
  selected?: boolean;
  /** Subtle random-feeling tilt, like a real wall of notes. */
  tilt?: boolean;
  /** Compact width for dense lanes (default md). */
  size?: "sm" | "md";
}

const TONES: Record<NonNullable<StickyNoteProps["tone"]>, string> = {
  yellow: "bg-warning-light border-warning/40",
  green: "bg-success-light border-success/40",
  blue: "bg-info-light border-info/40",
  orange: "bg-orange-light border-orange/40",
  red: "bg-danger-light border-danger/40",
  gray: "bg-neutral-light border-neutral/40",
};

export const StickyNote = forwardRef<HTMLDivElement, StickyNoteProps>(function StickyNote(
  {
    title,
    description,
    footer,
    adornment,
    tone = "yellow",
    selected = false,
    tilt = false,
    size = "md",
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <div
      ref={ref}
      {...rest}
      className={cn(
        "group relative rounded-sm border p-2.5 text-left shadow-sm transition-shadow",
        "hover:shadow-md",
        TONES[tone],
        size === "sm" ? "w-40" : "w-52",
        tilt && "odd:-rotate-1 even:rotate-1",
        selected && "ring-2 ring-brand ring-offset-1 ring-offset-background",
        className,
      )}
    >
      {adornment ? <div className="mb-1 flex flex-wrap items-center gap-1">{adornment}</div> : null}
      <div className="text-xs font-semibold leading-snug text-foreground">{title}</div>
      {description ? (
        <div className="mt-1 line-clamp-3 whitespace-pre-wrap text-[11px] leading-snug text-foreground/70">
          {description}
        </div>
      ) : null}
      {footer ? (
        <div className="mt-1.5 text-[10px] font-medium text-foreground/50">{footer}</div>
      ) : null}
      {children}
    </div>
  );
});
